// Gaveta lateral da conversa: card do chamado ligado a este ticket.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, CircularProgress, Drawer, IconButton, List, ListItem, ListItemText,
  TextField, Typography, makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import suporteApi, { mensagemErro } from "./api";
import { PainelCard, DialogCard, dataBR } from "./comum";
import CardFila from "./CardFila";

export const LARGURA_GAVETA = 320;
export const EVENTO_CARD = "suporte:card-atualizado";

// Avisa a gaveta (e o quadro) que o card de um ticket mudou
export const avisarCard = (ticketId) =>
  window.dispatchEvent(new CustomEvent(EVENTO_CARD, { detail: { ticketId: String(ticketId) } }));


const useStyles = makeStyles((theme) => ({
  // largura 0: a gaveta de contato original já reserva o espaço lateral (margem
  // negativa na conversa); esta só desenha o painel por cima desse espaço
  drawer: { width: 0, flexShrink: 0 },
  paper: {
    width: LARGURA_GAVETA, display: "flex",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)", borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)", borderTopRightRadius: 4, borderBottomRightRadius: 4,
  },
  header: {
    display: "flex", borderBottom: "1px solid rgba(0, 0, 0, 0.12)", alignItems: "center",
    padding: theme.spacing(0, 1), minHeight: "73px", gap: 4,
  },
  content: { padding: 12, height: "100%", overflowY: "auto", ...theme.scrollbarStyles },
  vazio: { display: "flex", flexDirection: "column", gap: 10 },
}));

const CardDrawer = ({ open, onClose, ticketId, pedidoAbrir }) => {
  const c = useStyles();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [qra, setQra] = useState("");
  const [texto, setTexto] = useState("");
  const [abrindo, setAbrindo] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(async (silencioso = false) => {
    if (!ticketId) return;
    if (!silencioso) setCarregando(true);
    try {
      const { data } = await suporteApi.get(`/api/whaticket/tickets/${ticketId}/card`);
      setDados(data);
      setQra((q) => q || (data.tecnico ? String(data.tecnico.qra) : ""));
    } catch (err) {
      if (!silencioso) toast.error(mensagemErro(err));
    }
    setCarregando(false);
  }, [ticketId]);

  useEffect(() => {
    setDados(null);
    setQra("");
    setTexto("");
  }, [ticketId]);

  useEffect(() => {
    if (!open) return undefined;
    carregar();
    const t = setInterval(() => carregar(true), 15000);
    const ouvir = (e) => e.detail?.ticketId === String(ticketId) && carregar(true);
    window.addEventListener(EVENTO_CARD, ouvir);
    return () => {
      clearInterval(t);
      window.removeEventListener(EVENTO_CARD, ouvir);
    };
  }, [open, carregar, ticketId]);

  // Clique no botão Card: se não houver card aberto e o contato for técnico, abre na hora.
  // (Contato que não é técnico: a gaveta pede o QRA.)
  useEffect(() => {
    if (!pedidoAbrir || !ticketId) return;
    (async () => {
      try {
        const { data } = await suporteApi.get(`/api/whaticket/tickets/${ticketId}/card`);
        setDados(data);
        if (!data.card && data.recente) {
          // card desta conversa finalizado há pouco: não abre outro sozinho (a gaveta pergunta)
        } else if (!data.card && data.modelo && !data.contato?.grupo) {
          const r = await suporteApi.post(`/api/whaticket/tickets/${ticketId}/card`, {});
          toast.success(`${data.modelo.nome} aberto`);
          setDados((d) => ({ ...d, card: r.data.card }));
        } else if (!data.card && data.tecnico && !data.contato?.grupo) {
          const r = await suporteApi.post(`/api/whaticket/tickets/${ticketId}/card`, { qra: data.tecnico.qra });
          toast.success(`Card aberto para QRA ${data.tecnico.qra}`);
          setDados((d) => ({ ...d, card: r.data.card }));
        }
      } catch (err) {
        toast.error(mensagemErro(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoAbrir]);

  const abrir = async () => {
    setAbrindo(true);
    try {
      const { data } = await suporteApi.post(`/api/whaticket/tickets/${ticketId}/card`, {
        qra: qra === "" ? null : Number(qra), texto, forcar: !!dados?.recente,
      });
      toast.success("Card aberto no quadro");
      setDados((d) => ({ ...d, card: data.card }));
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setAbrindo(false);
  };

  const card = dados?.card;
  const modelo = dados?.modelo;
  return (
    <Drawer
      className={c.drawer} variant="persistent" anchor="right" open={open}
      PaperProps={{ style: { position: "absolute" } }}
      classes={{ paper: c.paper }}
    >
      <div className={c.header}>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
        <Typography>
          {modelo ? `${modelo.nome}${card?.campos ? ` · ${Object.values(card.campos).find(Boolean) || ""}` : ""}`
            : card ? `Card · O.S ${card.os || "a informar"}` : "Card do atendimento"}
        </Typography>
      </div>
      <div className={c.content}>
        {carregando && !dados && <CircularProgress size={24} />}
        {card && modelo && (
          <CardFila card={card} modelo={modelo} onAtualizado={(novo) => setDados((d) => ({ ...d, card: novo }))} />
        )}
        {card && !modelo && (
          <PainelCard card={card} onAtualizado={(novo) => setDados((d) => ({ ...d, card: novo.status === "ABERTO" || novo.status === "PENDENTE" || novo.protocolo ? novo : null }))} />
        )}
        {dados && !card && modelo && (
          <div className={c.vazio}>
            <Typography variant="body2">{`Nenhum card de ${modelo.nome.toLowerCase()} aberto para esta conversa.`}</Typography>
            <Button variant="contained" color="primary" disabled={abrindo} onClick={async () => {
              setAbrindo(true);
              try {
                const { data } = await suporteApi.post(`/api/whaticket/tickets/${ticketId}/card`, {});
                setDados((d) => ({ ...d, card: data.card }));
              } catch (err) {
                toast.error(mensagemErro(err));
              }
              setAbrindo(false);
            }}>
              {`Abrir ${modelo.nome.toLowerCase()}`}
            </Button>
          </div>
        )}
        {dados && !card && !modelo && (
          <div className={c.vazio}>
            <Typography variant="body2">Nenhum card aberto para esta conversa.</Typography>
            {dados.recente && (
              <div style={{ border: "1px solid #ff9800", borderRadius: 6, padding: 10, background: "rgba(255,152,0,.08)" }}>
                <Typography variant="body2">
                  <b>{`Card #${dados.recente.id} desta conversa foi finalizado às ${dataBR(dados.recente.fim)}`}</b>
                  {` (O.S ${dados.recente.os || "-"}${dados.recente.conta ? ` · conta ${dados.recente.conta}` : ""}).`}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ margin: "4px 0 8px" }}>
                  Precisa completar a conta ou registrar uma informação? Abra o card finalizado. Só abra um card
                  novo se for outro serviço.
                </Typography>
                <Button size="small" variant="contained" color="primary" onClick={() => setDetalhe(dados.recente.id)}>
                  {`Completar card #${dados.recente.id}`}
                </Button>
              </div>
            )}
            <Typography variant="body2" color="textSecondary">
              {dados.tecnico
                ? `Técnico identificado: QRA ${dados.tecnico.qra} — ${dados.tecnico.nome} (${dados.tecnico.status})`
                : `${dados.contato?.nome || "Contato"} não está na Base técnico. Informe o QRA para abrir o card.`}
            </Typography>
            <TextField label="QRA" type="number" size="small" variant="outlined" value={qra}
              onChange={(e) => setQra(e.target.value)} />
            <TextField label="Descrição (opcional)" size="small" variant="outlined" multiline rows={2}
              value={texto} onChange={(e) => setTexto(e.target.value)}
              helperText="Se citar a O.S (059127/26) ou 'conta B555', já preenche." />
            <Button variant={dados.recente ? "outlined" : "contained"} color="primary" onClick={abrir} disabled={abrindo}>
              {dados.recente ? "Abrir card novo (outro serviço)" : "Abrir card"}
            </Button>
          </div>
        )}
        {dados?.anteriores?.length > 0 && (
          <>
            <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 16 }}>
              {modelo ? "ANTERIORES DESTE NÚMERO" : "ATENDIMENTOS ANTERIORES"}
            </Typography>
            <List dense>
              {dados.anteriores.map((a) => (
                <ListItem key={a.id} button onClick={() => setDetalhe(a.id)}>
                  <ListItemText
                    primary={modelo ? `${a.titulo || modelo.nome} · ${a.etapa || "-"}` : `O.S ${a.os || "-"} · ${a.tipo || ""} ${a.sistema || ""}`}
                    secondary={modelo ? dataBR(a.fim || a.inicio) : `${dataBR(a.fim || a.inicio)} · ${a.protocolo || a.protocolo_legado || a.status}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </div>
      <DialogCard id={detalhe} open={!!detalhe} onClose={() => { setDetalhe(null); carregar(true); }} />
    </Drawer>
  );
};

export default CardDrawer;
